import { createTheme } from "@mui/material/styles";

/**
 * Opt-in UI standard, shared with the IPAS portal and the Self-Service app:
 * contained = brand orange with white text, outlined = orange outline on
 * transparent, text = orange text.
 *
 * Off by default, so production (the claims system) keeps the look it has today:
 * white button text with `outlined` rendered as a dark filled pill.
 *
 *   REACT_APP_UI_STANDARD=on  -> new standard, system-wide (IFAS *and* claims).
 *                               Set this in staging to evaluate it.
 *   unset / anything else     -> current production styling, unchanged.
 *
 * Once signed off in staging, turn it on in production, then fold these styles in
 * and delete the flag.
 */
const uiStandard =
  String(process.env.REACT_APP_UI_STANDARD || "").toLowerCase() === "on";

const theme = createTheme({
  palette: {
    mode: "light",
    grey: {
      100: "#F5F5F5",
      200: "#DFDDDD",
      500: "#0000004D", // use this in `divider`
    },
    primary: {
      main: "#EC6E00", // button background
      contrastText: "#FFFFFF", // button text
    },
    secondary: {
      main: "#EC4200",
    },
    text: {
      primary: "#333333",
      secondary: "#000000",
      disabled: "#898989",
    },
    background: {
      default: "#FFFFFF",
      paper: "#F7F7F7",
    },
  },
  typography: {
    fontFamily: "Montserrat",
    allVariants: {
      color: "#333333", // applies to all text elements
    },
    h1: {
      fontWeight: 600,
      fontSize: "55px",
      lineHeight: "100%",
      letterSpacing: "0em",
    },
    h2: {
      fontWeight: 600,
      fontSize: "30px",
      lineHeight: "40px",
      letterSpacing: "0em",
      textAlign: "center",
      color: "#000000",
    },
    h5: {
      fontWeight: 700,
      fontSize: "18px",
      lineHeight: 1.4,
      letterSpacing: "0em",
      textAlign: "center",
    },
    h6: {
      fontWeight: 400,
      fontSize: "16px",
      lineHeight: "26px",
      letterSpacing: "0em",
      textAlign: "center",
      color: "#000000",
    },
  },
  shape: {
    borderRadius: 4,
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          color: uiStandard ? "#EC6E00" : "#FFFFFF",
          fontFamily: "Montserrat",
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "30px",
          borderRadius: 25,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        ...(uiStandard
          ? {
              contained: { color: "#FFFFFF" },
              outlined: {
                color: "#EC6E00",
                borderColor: "#EC6E00",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "#FFF4EB",
                  borderColor: "#D94F00",
                },
              },
              text: {
                color: "#EC6E00",
                "&:hover": { backgroundColor: "#FFF4EB" },
              },
            }
          : {}),
      },
      variants: [
        {
          props: { variant: "success" as any },
          style: {
            backgroundColor: "#20BF55",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#1a9f47",
            },
          },
        },
        {
          props: { variant: "error" as any },
          style: {
            backgroundColor: "#E8003F",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#c00035",
            },
          },
        },
        ...(uiStandard
          ? []
          : [
              {
                props: { variant: "outlined" as const },
                style: {
                  backgroundColor: "#333333",
                  color: "#FFFFFF",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "#000000",
                  },
                },
              },
            ]),
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          //border: "1px solid #0000004D",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "1px solid #0000004D", // hover
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "1px solid #0000004D", // focus
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontWeight: 600,
        },
      },
    },
  },
});

export default theme;
